import { ArrowUpRight, EyeOff, Loader2, MoreHorizontal, Pencil, Radar, Undo2 } from "lucide-react";
import { Link } from "react-router-dom";

import { Button } from "@/shared/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shared/ui/dropdown-menu";
import { Switch } from "@/shared/ui/switch";
import { stripInternalMarkers } from "@/shared/lib/internalCodes";
import { cn } from "@/shared/lib/utils";

import {
  runViaOf,
  type AcqCatalogItem,
  type AcqPrefLevel,
  type AcqScene,
} from "../../api/acqService";
import {
  archetypeLabel,
  CADENCE_LABEL,
  cardTopicWords,
  COMMENT_NO_TOPIC_HINT,
  CONTENT_KPI_NOTE,
  discoveryWhyText,
  explainZeroRun,
  explainZeroRunByFunnel,
  INTENT_HINT_PREFIX,
  intentTypeLabel,
  isContentArchetype,
  lacksCommentTopicWords,
  LEVEL_LABEL,
  peerFeedbackText,
  TOPIC_WORDS_LABEL,
  TOPIC_WORDS_MAX,
} from "../../model/acqLabels";
import { discoverPriceText, useDiscoverPrice } from "../../model/useDiscoverPrice";
import { formatElapsed, heldByColleagueNote } from "../../model/useDiscoverRun";

/** 卡上的时间戳:`2026-09-27T00:19:00` → `09-27 00:19`。 */
function shortTs(iso: string): string {
  return iso.replace("T", " ").slice(5, 16);
}

/**
 * 「上次」那一行下面要不要多一句原因(0927 异步化,读 `lastRun`)。
 * ★ 只在终态说:0 张 → 漏斗那句(与跑完时的 toast 同一套文案);没跑完 → 后端那句人话。
 *   在跑 / 老后端不发 / 出了单 ⇒ null,不多占一行。
 */
/**
 * 卡上「上次」那一行。
 * ★1006 演示机最终回归:卡写「上次:10-06 14:56,10 条」,刚弹出的 toast 却是「新进 0 条」——
 *   pref 的 `lastResultCount` 是 api `recordRun(inserted + updated)`,10 条全是库里已有、被刷新的;
 *   toast 读的是 `result.inserted`。卡上按同一口径报「新进 N 条」,已在库里的另说一句。
 * ★ 最近一轮没跑完时说「没跑完」而不是沿用 pref 里更早那次的条数:那会把一次失败显示成一次成功。
 * 没有 lastRun 回执(老后端 / 在跑)⇒ 照旧读 lastRunAt / lastResultCount。
 */
function lastRunLine(item: AcqCatalogItem): string {
  const last = item.lastRun;
  if (last?.status === "FAILED") return `上次：${shortTs(last.startedAt)}，没跑完`;
  if (last?.status === "SUCCESS" && last.result) {
    const r = last.result;
    const again =
      r.updated > 0
        ? `（另有 ${r.updated} 条已在库里${heldByColleagueNote(r.heldByColleague)}）`
        : "";
    return `上次：${shortTs(item.lastRunAt ?? last.finishedAt ?? last.startedAt)}，新进 ${r.inserted} 条${again}`;
  }
  return item.lastRunAt
    ? `上次：${shortTs(item.lastRunAt)}，${item.lastResultCount ?? 0} 条`
    : "还没跑过";
}

function lastRunNote(item: AcqCatalogItem): string | null {
  const last = item.lastRun;
  if (!last) return null;
  if (last.status === "FAILED") return last.error ?? "上一轮没跑完";
  const r = last.result;
  if (last.status !== "SUCCESS" || !r || r.inserted + r.updated > 0) return null;
  return (
    explainZeroRunByFunnel(r.funnel, r.rejectBreakdown) ??
    explainZeroRun(r.rawCount, r.rejectBreakdown)
  );
}

/** 关键词最多露 6 个:再多这张卡就变成一个词库,而它要回答的是「这儿值不值得找一次」。 */
const KEYWORD_CAP = 6;

/** 卡上最多露几个场景名(契约 §16.1:≤2,多余「+N」)。 */
const SCENE_CAP = 2;

/** 没归到任何产品的词落这一组(契约 §13.6:无产品的归「通用」)。 */
const GENERIC_GROUP = "通用";

const LEVEL_CLASS: Record<string, string> = {
  P0: "border-primary/40 bg-primary/10 text-primary",
  P1: "border-border bg-foreground/5 text-foreground/80",
  P2: "border-warning/40 bg-warning/10 text-warning",
  NA: "border-dashed border-border bg-transparent text-muted-foreground",
};

/**
 * 关键词按产品分组(契约 §13.6)。
 *
 * ★★ `keywords` 是**扁平并集,采集只读它**(契约 §13.3):所以这里以 `keywords` 为准,
 *   分组表只决定"这个词挂在哪个产品下面"。反过来以 `keywordsByProduct` 为准的话,
 *   用户手加的词(只进扁平表)在面上会**整个消失** —— 他会以为没保存上,再加一遍。
 * ★ 分组表里有、扁平表里没有的词不显示:那是一次没跟上的推断留下的影子,
 *   显示它等于告诉用户"这个词在跑",而采集根本读不到它。
 */
export function groupKeywords(
  item: Pick<AcqCatalogItem, "keywords" | "keywordsByProduct">,
  nameOf: (code: string) => string
): { title: string; keywords: string[] }[] {
  const flat = item.keywords ?? [];
  const map = item.keywordsByProduct ?? {};
  const groups: { title: string; keywords: string[] }[] = [];
  const claimed = new Set<string>();
  for (const [code, words] of Object.entries(map)) {
    const hit = (words ?? []).filter((w) => flat.includes(w));
    if (hit.length === 0) continue;
    hit.forEach((w) => claimed.add(w));
    groups.push({ title: nameOf(code), keywords: hit });
  }
  const rest = flat.filter((w) => !claimed.has(w));
  // ★ 一个组都没有时**不显示「通用」这个标题**:所有词都在一组里的时候,
  //   那个标题只是一行没有信息的字(目录 17 张卡上就是 17 行废话)。
  // ★★ 0921:「通用」组排**最前**。用户手加的词都落在这一组,而卡上只露前 6 个词 ——
  //   排最后的话 AI 的词先把 6 个名额占满,他加的词一个都露不出来
  //   (用户反馈原话:「加了关键词…也没有显示到卡片上」)。采集取词也是通用词先进预算,两边同序。
  if (rest.length > 0)
    groups.unshift({ title: groups.length > 0 ? GENERIC_GROUP : "", keywords: rest });
  return groups;
}

/**
 * 目录里的一张卡 = 一种「去哪里找」。
 *
 * ★★ `COMING_SOON` **不露「发现一次」按钮,但推荐档照显**(契约 §12.10)。
 *   产品要展示广度(17 种,这是"功能强大"的实证),但不能展示假能力 ——
 *   一个点了没反应的按钮比没有那个按钮坏得多。
 * ★ `runVia="link"` 的两种(地图门店 / 行业趋势)出口是既有页面,按钮写「去看」而不是
 *   「发现一次」—— 它们不在这里计价,写价签会凭空多出一笔不存在的账。
 * ★ 价三态:没价「按次计费」/ 0「免费」/ N「N 积分」。**永不编数**。
 * ★ 0917 批次二(契约 §13.6):右上角「···」里是**主推可调**与「不看这个」——
 *   AI 推的档是建议不是判决,用户比我们更知道他的客户在哪儿。
 */
export function DiscoveryTypeCard({
  item,
  running,
  elapsedSec = null,
  productName,
  onRun,
  onEditKeywords,
  onToggleSubscribe,
  onSetLevel,
  onSetHidden,
  onSetProductLevel,
  scenes = [],
  highlighted = false,
  focusProduct = "",
  focusWords,
  todayFull = null,
}: {
  item: AcqCatalogItem;
  running: boolean;
  /**
   * 1006:今日商机已满的那句人话(页面按今日 `total >= cap` 判)。
   * ★ 1023 起**只提示不禁用**:api 1009 就撤了「已满拒发」(一轮一口价,判中的都出卡,多的进线索库),
   *   这里还禁着按钮 = 测试机租户满 30 条后再也点不了「发现一次」。第一方 0 价线(runVia=api)不提示。
   */
  todayFull?: string | null;
  /**
   * 正在跑的这一轮已经等了几秒(0927 异步化)。有它按钮写「正在找…（已等 m:ss）」;
   * `null` = 调用方不知道(只写「正在找…」)。一轮 3~6 分钟,不报时长用户会以为卡住了。
   */
  elapsedSec?: number | null;
  /** `node_code` → 产品名。取不到回落到 code 本身(见 `useTenantProducts`)。 */
  productName: (code: string) => string;
  onRun: (type: string) => void;
  onEditKeywords: (item: AcqCatalogItem) => void;
  onToggleSubscribe: (item: AcqCatalogItem, next: boolean) => void;
  onSetLevel: (item: AcqCatalogItem, level: AcqPrefLevel) => void;
  onSetHidden: (item: AcqCatalogItem, hidden: boolean) => void;
  /**
   * 0918 批次六(契约 §17.2):**只改这个产品下的档**(写 `productLevels`,全局档不动)。
   * 只在选中了产品时用得上;「全部」页签下这几项不出现 —— 那时没有"哪个产品"可说。
   */
  onSetProductLevel?: (item: AcqCatalogItem, level: AcqPrefLevel) => void;
  /**
   * 引用了这条线的场景(契约 §16.1)。**调用方已经按选中的产品筛过了** ——
   * 卡自己不知道页面此刻停在哪个页签上,那个判据只有一处(页面),不该有第二处。
   */
  scenes?: AcqScene[];
  /** 场景 chip 选中且这条线被它引用 ⇒ 描边点亮(契约 §16.1「点一枚高亮引用它的卡」)。 */
  highlighted?: boolean;
  /** 选中的产品页签(`""` = 全部)。 */
  focusProduct?: string;
  /**
   * 这条线给选中产品配的词(契约 §16.2)。`undefined` = 停在「全部」上,词照旧按产品分组显示。
   * ★ 空数组 = **这条线没给这个产品配词** —— 那时词区灰一行、「发现一次」禁用:
   *   一个没有词的采集跑出去只会烧掉一次钱换回 0 条,而用户以为是"这个地方没人"。
   */
  focusWords?: string[];
}) {
  const price = useDiscoverPrice(item);
  const soon = item.status === "COMING_SOON";
  const hidden = !!item.hidden;
  // ★ 大小写归一走 `runViaOf`(api 发的是大写)。`api` 型(第一方意向)在面上与 `ai`
  //   一视同仁:照样有「发现一次」和「订阅自动找」——(契约 §14.1)它只是不经 ai 那一跳。
  const runVia = runViaOf(item);
  const intents = item.intentTypes ?? [];
  const focused = !!focusProduct && focusWords != null;
  // 1009(契约 §5):评论原型有话题词 ⇒ 卡面只摆话题词(api 取词也是 topic_words 优先,
  //   与产品无关 ⇒ 产品页签下同样摆它);没有 ⇒ 回落下面的关键词逻辑,逐字不变。
  const topicWords = cardTopicWords(item);
  // 选中产品时只摆这个产品的词组(组标题就是产品名);「全部」上照旧按产品分组。
  const groups = topicWords
    ? [{ title: TOPIC_WORDS_LABEL, keywords: topicWords }]
    : focused
      ? focusWords.length > 0
        ? [{ title: "", keywords: focusWords }]
        : []
      : groupKeywords(item, productName);
  // 1023:话题词不折叠 —— 它本来就只有 ≤5 个;折起来的话用户刚加的那个永远在「还有 N 个」里。
  const shown = topicWords ? { groups, hiddenCount: 0 } : capGroups(groups, KEYWORD_CAP);
  const peer = peerFeedbackText(item.peerFeedback);
  /** 今日商机已满 ⇒ 付费线按钮旁提示「新的进线索库」(1023 起不禁用,见 `todayFull` 注释)。 */
  const fullHint = !!todayFull && runVia !== "api" && !running;
  /**
   * 「这条线没给这个产品配词」⇒ 不许跑(契约 §16.2)。
   * ★★ `api` 型(第一方意向)**不受这条管**:它根本不按关键词去搜,扫的是自家客服会话
   *   (契约 §14.1 里它的 keywordBudget 就是空的)。按"没配词"把它禁掉,等于把一条
   *   免费且能用的线在按产品看的时候整个关掉,而面上给的理由是错的。
   */
  /** 这张卡此刻有没有「发现一次」这枚按钮(COMING_SOON / 已隐藏 / link 型都没有)。 */
  const canRun = !soon && !hidden && runVia !== "link";
  const noWordsForProduct =
    !topicWords && focused && focusWords.length === 0 && runVia !== "api" && canRun;
  /** 评论线没配话题词(1027):只提示不禁用 —— 产品名兜底常常是能用的,拦掉会误伤。 */
  const noTopicWords = canRun && !noWordsForProduct && lacksCommentTopicWords(item);
  const sceneTitles = scenes.map((s) => s.title).filter(Boolean);
  const contentLine = isContentArchetype(item.archetype);
  /** 这个产品下用户手调过的档(契约 §17.2:`productLevels` 优先于场景重算)。 */
  const productLevel = focusProduct ? (item.productLevels?.[focusProduct] ?? null) : null;
  /** 卡上摆出来的这几个场景里有没有「材料不足,AI 猜的」那种(契约 §17.2)。 */
  const anyWeak = scenes.slice(0, SCENE_CAP).some((s) => s.weak === true);
  /**
   * 「订阅自动找」在这个产品下开着吗(契约 §17.3.1)。
   *
   * ★ 后端还没上 `subscribedProducts` 时**退回全局那一格** —— 不假装"这个产品没订",
   *   那会把一个每天都在跑的订阅显示成关着的,用户会再开一次。
   * ★★ 但这时候**标签也要跟着退**:写「只为「X」自动找」而状态其实是全局的,
   *   等于对着一个全局订阅说它只订了一个产品 —— 那是页面在替后端撒谎。
   *   所以字段在场才敢说"只为 X",不在场就老实写「不分产品」。
   */
  const perProductSubs = Array.isArray(item.subscribedProducts) ? item.subscribedProducts : null;
  const subscribedHere =
    focusProduct && perProductSubs ? perProductSubs.includes(focusProduct) : item.subscribed;
  const subscribeLabel = !focusProduct
    ? "订阅自动找"
    : perProductSubs
      ? `只为「${productName(focusProduct)}」自动找`
      : "订阅自动找（不分产品）";

  return (
    <article
      className={cn(
        "flex flex-col gap-3 rounded-xl border bg-card p-4 shadow-sm",
        (soon || hidden) && "opacity-70",
        // ★ 高亮用描边 + 一点底色,不改卡的位置:位置一动,用户会以为列表被筛过了。
        highlighted && "border-primary bg-primary/5 ring-1 ring-primary/30"
      )}
    >
      {/* ── 抬头:名字 + 原型 + 推荐档 + 「···」 ────────────────────────── */}
      <header className="space-y-1.5">
        <div className="flex flex-wrap items-start gap-x-2 gap-y-1">
          <h3 className="min-w-0 flex-1 basis-full text-sm font-bold leading-snug sm:basis-0">
            {item.name}
          </h3>
          <span
            className={cn(
              "shrink-0 rounded-full border px-1.5 py-px text-2xs font-semibold",
              // ★★ 真渲染抓到的:按产品看的时候,一张落在**主推**区的卡右上角还挂着全局的
              //   「不建议」pill —— 同一张卡上两个相反的判断,读的人只能猜哪个算数。
              //   焦点模式下这枚 pill 改成中性底色 + 「全局」前缀:信息不丢(用户设过的
              //   全局档还看得见),但它不再假装是"这个产品下的档位"——那个由分区标题说。
              focused
                ? "border-border bg-muted text-muted-foreground"
                : (LEVEL_CLASS[item.level] ?? LEVEL_CLASS.NA)
            )}
            title={focused ? "这是不分产品时的推荐档；这个产品下的档位看它落在哪一区" : undefined}
          >
            {focused && <span className="font-normal opacity-70">全局 </span>}
            {LEVEL_LABEL[item.level]}
            {/* ★ 这个产品下用户自己定过档 ⇒ 说出来(它优先于场景重算,决定了这张卡落在哪一区)。 */}
            {productLevel && (
              <span className="ml-1 font-normal opacity-80">
                · 你给「{productName(focusProduct)}」定了{LEVEL_LABEL[productLevel]}
              </span>
            )}
            {/* ★ 「你定的」不是装饰:它是那条"重新推断不覆盖你改过的档"的可见证据。
                没有它,用户点完「设为主推」看不出与 AI 推的 P0 有什么不同,
                下次重新推断后也无从判断到底覆没覆盖。 */}
            {item.levelSource === "user" && (
              <span className="ml-1 font-normal opacity-80">你定的</span>
            )}
          </span>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="-my-1 h-7 w-7 shrink-0"
                aria-label={`${item.name} 的更多操作`}
              >
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              {/* ★★ 0918 批次六(契约 §17.2):选中产品时**先摆这个产品的那三项** ——
                  用户此刻看的是「X」这一档,他点「设为主推」想的就是"对 X 来说主推",
                  而不是把整个租户的全局档改掉。两组并存、各自标题写清管到哪儿。 */}
              {focusProduct && onSetProductLevel && (
                <>
                  <DropdownMenuLabel className="text-2xs font-normal text-muted-foreground">
                    在「{productName(focusProduct)}」下
                  </DropdownMenuLabel>
                  <DropdownMenuItem
                    disabled={productLevel === "P0"}
                    onSelect={() => onSetProductLevel(item, "P0")}
                  >
                    设为「{productName(focusProduct)}」的主推
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    disabled={productLevel === "P1"}
                    onSelect={() => onSetProductLevel(item, "P1")}
                  >
                    设为可试
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    disabled={productLevel === "NA"}
                    onSelect={() => onSetProductLevel(item, "NA")}
                  >
                    这个产品下不看
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                </>
              )}
              <DropdownMenuLabel className="text-2xs font-normal text-muted-foreground">
                {focusProduct ? "不分产品(全局)" : "推荐档由你说了算"}
              </DropdownMenuLabel>
              <DropdownMenuItem
                disabled={item.level === "P0"}
                onSelect={() => onSetLevel(item, "P0")}
              >
                设为主推
              </DropdownMenuItem>
              <DropdownMenuItem
                disabled={item.level === "P1"}
                onSelect={() => onSetLevel(item, "P1")}
              >
                设为可试
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              {hidden ? (
                <DropdownMenuItem onSelect={() => onSetHidden(item, false)}>
                  <Undo2 className="h-4 w-4" />
                  恢复显示
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem onSelect={() => onSetHidden(item, true)}>
                  <EyeOff className="h-4 w-4" />
                  不看这个
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-2xs text-muted-foreground">
          <span className="rounded bg-foreground/5 px-1.5 py-px font-semibold">
            {archetypeLabel(item.archetype)}
          </span>
          <span>{item.description}</span>
        </p>
        {/* ★ 「多为」回答的是目录页最贵的那个问题:花 180 积分跑一次,**回来的是什么人**。
            名字和一句话说的是"去哪儿找",这一行说的是"找回来的人想干嘛"——
            一个只做资质申报的公司看到「多为:问产品」就该知道这一种不是他的主战场。
            ★ 缺席整行不出现,不写「暂无」(一行"暂无"占着位置却不给信息)。 */}
        {intents.length > 0 && (
          <p className="text-2xs text-muted-foreground">
            {INTENT_HINT_PREFIX}
            <span className="font-semibold text-foreground/80">
              {intents.map(intentTypeLabel).join(" / ")}
            </span>
          </p>
        )}
        {/* ★ 「场景:…」(契约 §16.1):这一行说的是**为什么这条线在这儿** ——
            `why` 讲的是 AI 的理由(一段话),这一行讲的是它挂在哪个场景上(可点选的那个)。
            ★ 最多两个 + 「+N」:三个以上就把卡抬头挤成一段文字,而它是要扫的。
            ★ 一个场景都没引用 ⇒ 整行不出现,**不写「暂无场景」**:那句话的意思正好是
            "这条线这一版没人给它理由",而那已经由它落在「更多」区表达过了。 */}
        {sceneTitles.length > 0 && (
          <p className="text-2xs text-muted-foreground">
            场景：
            <span className={cn("font-semibold", anyWeak ? "opacity-70" : "text-foreground/80")}>
              {sceneTitles.slice(0, SCENE_CAP).join(" · ")}
            </span>
            {sceneTitles.length > SCENE_CAP && (
              <span className="ml-1" title={sceneTitles.join(" · ")}>
                +{sceneTitles.length - SCENE_CAP}
              </span>
            )}
            {/* ★★ 契约 §17.2:`weak` 的场景是"资料不够、模型猜的" —— 灰着标出来,
                而且它**不参与定档**(见 `strongScenesOf`)。不标的话,用户会把一条
                没有证据的理由当成"AI 从我的资料里读出来的"。 */}
            {anyWeak && (
              <span
                className="ml-1 rounded border border-dashed px-1 py-px opacity-70"
                title="这个产品的资料里没读到对应段落，这条是 AI 按产品名猜的；它不参与推荐档"
              >
                材料不足，AI 猜的
              </span>
            )}
          </p>
        )}
      </header>

      {/* ── 为什么推荐它(引用 KB / 行业包)──────────────────────────── */}
      {item.why && (
        <p className="rounded-lg border border-dashed bg-muted/40 px-2.5 py-2 text-xs leading-relaxed text-muted-foreground">
          {/* 1004 T10:理由原文带「(K22)」「词典P0」「node_code: xx」这类内部标记,对客剥掉 */}
          {discoveryWhyText(stripInternalMarkers(item.why))}
        </p>
      )}

      {/* ★★ 内容线的 KPI 口径(契约 §16.4)。知乎提问 / 评论区 / 搜一搜抓回来的是
          **可回答的问题**,不是一条能打电话的线索 —— 拿"这轮 0 条"去判它的死刑,
          等于把一条正在正常工作的线关掉。这一行摆在卡上而不是只在 toast 里,
          是因为用户在**点之前**就该知道这条线是按什么算账的。 */}
      {contentLine && (
        <p className="rounded-lg border border-dashed border-primary/30 bg-primary/5 px-2.5 py-1.5 text-2xs leading-relaxed text-muted-foreground">
          {CONTENT_KPI_NOTE}
        </p>
      )}

      {/* ── 关键词:按产品分组(契约 §13.6) ──────────────────────────────
          ★★ `api` 型(第一方意向)**整块不出现**:那条线不按关键词去搜,它扫的是自家客服
          会话(契约 §14.1 里它的 keywordBudget 就是空的)。摆一个「还没有关键词 · 编辑」
          在那儿,用户会往里加词 —— 而那些词一个都不会被读到,面上也没有任何一处告诉他。
          ★ 判据只好用 `runVia`:**卡的 DTO 里没有 keywordBudget 这一格**。真正该有的是
          目录行自己说"我吃不吃关键词"(挂账给联调)。 */}
      {runVia !== "api" && (
        <div className="space-y-1.5">
          {shown.groups.length === 0 ? (
            // ★ 两句话分开说(契约 §16.2):停在「全部」上一个词都没有 = 这条线还没配词;
            //   停在某个产品上没有 = **这条线没给这个产品配词**(别的产品可能有一堆)。
            //   合成一句「还没有关键词」的话,用户会去给一条其实词很全的线再加一遍词。
            // ★ 只有**真能跑**的卡才用警示色说这句:COMING_SOON / 已隐藏 / link 型本来就
            //   没有「发现一次」,对它们喊"没给这个产品配词"是在指一个用户此刻做不了的动作
            //   (真渲染抓到的:「即将开通」那一整区当时全是橙字)。
            <span
              className={cn(
                "text-2xs",
                noWordsForProduct ? "text-warning" : "text-muted-foreground"
              )}
            >
              {focused ? `这条线没给「${productName(focusProduct)}」配词` : "还没有关键词"}
            </span>
          ) : (
            shown.groups.map((g) => (
              <div key={g.title || "__flat__"} className="flex flex-wrap items-center gap-1.5">
                {g.title && (
                  // ★ 真渲染抓到的:标题原本是一段和 chip 同号的小字,一行读下来
                  //   「商标注册 商标怎么注册 被驳回了」分不出哪个是组名 ——
                  //   加一个冒号 + 深一档的颜色,它才读得出是**标签**不是又一个词。
                  <span className="shrink-0 text-2xs font-semibold text-foreground/70">
                    {g.title}：
                  </span>
                )}
                {g.keywords.map((k, i) =>
                  // ★ 老数据超过上限的那几个:摆出来但灰掉 —— 每轮只搜前 TOPIC_WORDS_MAX 个,藏起来他就不知道要删。
                  topicWords && i >= TOPIC_WORDS_MAX ? (
                    <span
                      key={k}
                      title={`超过 ${TOPIC_WORDS_MAX} 个，不会搜`}
                      className="rounded-full border border-dashed px-2 py-px text-2xs text-muted-foreground line-through"
                    >
                      {k}
                    </span>
                  ) : (
                    <span key={k} className="rounded-full border bg-background px-2 py-px text-2xs">
                      {k}
                    </span>
                  )
                )}
              </div>
            ))
          )}
          <div className="flex flex-wrap items-center gap-x-2">
            {topicWords && topicWords.length > TOPIC_WORDS_MAX && (
              <span data-testid="topic-over-cap" className="text-2xs text-warning">
                只搜前 {TOPIC_WORDS_MAX} 个，划掉的不会搜
              </span>
            )}
            {shown.hiddenCount > 0 && (
              <span className="text-2xs text-muted-foreground">还有 {shown.hiddenCount} 个</span>
            )}
            <button
              type="button"
              onClick={() => onEditKeywords(item)}
              className="inline-flex items-center gap-0.5 text-2xs font-semibold text-primary hover:underline"
            >
              <Pencil className="h-3 w-3" />
              编辑
              {item.keywordsSource === "user" && (
                <span className="ml-0.5 font-normal text-muted-foreground">（你改过）</span>
              )}
            </button>
          </div>
        </div>
      )}

      {/* ── 上次跑成什么样 + 同行业反馈 ───────────────────────────────── */}
      <div className="space-y-0.5 text-2xs text-muted-foreground">
        <p>
          {/* ★ 最近一轮没跑完时说「没跑完」而不是沿用 pref 里更早那次的「N 条」:
              那会把一次失败显示成一次成功。在跑 / 出了单 / 老后端 ⇒ 照旧读 lastRunAt。 */}
          {lastRunLine(item)}
        </p>
        {/* 0 张为什么 / 没跑完为什么(0927):与跑完那一刻的 toast 同一句,刷新后还在卡上。 */}
        {lastRunNote(item) && (
          <p className="line-clamp-2" title={lastRunNote(item) ?? undefined}>
            {lastRunNote(item)}
          </p>
        )}
        {/* ★ 全 0 时 `peerFeedbackText` 回 null ⇒ 整行不出现:显示「成了 0 · 找错人 0」
            会被读成"这条线试过了,不行",而事实是同行里还没人试过。 */}
        {peer && <p>{peer}</p>}
      </div>

      {/* ── 主按钮 + 订阅开关 ─────────────────────────────────────────── */}
      <div className="mt-auto space-y-2.5 border-t pt-3">
        {hidden ? (
          // ★ 隐藏的那几种不摆「发现一次」:用户刚说了不看它,再给一个花钱的按钮是在劝他反悔。
          //   这里只留一条回头路。
          <Button
            variant="outline"
            size="sm"
            className="w-full"
            onClick={() => onSetHidden(item, false)}
          >
            <Undo2 className="h-4 w-4" />
            恢复显示
          </Button>
        ) : soon ? (
          // ★ 没有按钮。说清"接通了会出现什么"比摆一个点了没反应的按钮诚实 ——
          //   用户由此知道这格空着是有意的,不是页面没加载出来。
          <p className="text-xs text-muted-foreground">接通后这儿会出现「发现一次」</p>
        ) : runVia === "link" ? (
          <Button asChild variant="outline" size="sm" className="w-full">
            <Link to={item.linkTo ?? "/ai-huoke"}>
              去看
              <ArrowUpRight className="h-4 w-4" />
            </Link>
          </Button>
        ) : (
          <>
            <Button
              size="sm"
              className="w-full"
              disabled={running || noWordsForProduct}
              onClick={() => onRun(item.type)}
              title={
                noWordsForProduct
                  ? `这条线没给「${productName(focusProduct)}」配词`
                  : "一轮要几分钟，在后台跑，跑完这张卡会提示结果；一条都没发现不扣分"
              }
            >
              {running ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Radar className="h-4 w-4" />
              )}
              {running
                ? elapsedSec != null
                  ? `正在找…（已等 ${formatElapsed(elapsedSec)}）`
                  : "正在找…"
                : `发现一次 · ${discoverPriceText(price)}`}
            </Button>
            {/* ★ 禁用态**必须把原因写在旁边**:一个灰着的按钮要 hover 才知道为什么点不动,
                而用户的第一反应是"页面坏了"。
                ★★ 0921 起「自己加词」是管用的第三条路:抽屉加的词落「通用」组,而通用词
                **对每个产品都生效**(后端取词与 `productKeywords` 同口径)—— 加完这张卡在这个
                产品下就有词可搜了。此前这里故意不提加词,因为那时加了也进不了产品视图。 */}
            {fullHint && (
              <p data-testid="acq-today-full" className="text-2xs leading-relaxed text-amber-600">
                {`${todayFull}：照样能找，新找到的进「线索库」；在「今日商机」里接手或标记过的会腾出名额`}
              </p>
            )}
            {noTopicWords && (
              <p data-testid="comment-no-topic" className="text-2xs leading-relaxed text-warning">
                {COMMENT_NO_TOPIC_HINT}点「编辑」填上。
              </p>
            )}
            {noWordsForProduct && (
              <p className="text-2xs leading-relaxed text-muted-foreground">
                {/* ★★ 措辞照后端真实行为写:api 在 `keywordsByProduct[code]` 为空时
                    **退回这条线的全部词**照跑(契约 §16.2 api 落地形状),不是跑成 0 条。
                    所以这里拦的不是"跑了也没用",是"那一轮不是按这个产品找的" ——
                    他会照着页签上的产品名理解这次花的钱,而跑出来的是另一回事。 */}
                跑起来会退回用这条线的全部词，那不是「按这个产品找」。切回「全部」跑，
                或者在页面顶上重新推断一次(按产品分词是推断出来的)，也可以点「编辑」自己加几个词
                (你加的词对每个产品都生效)。
              </p>
            )}
          </>
        )}

        {!soon && !hidden && runVia !== "link" && (
          <label className="flex items-center justify-between gap-2">
            <span className="min-w-0 text-2xs text-muted-foreground">
              {/* ★ 0918 批次六(契约 §17.3.1):产品页签下订的是**这个产品** ——
                  开关旁边必须写出来订的是谁,否则用户以为自己订的是整条线。 */}
              {subscribeLabel}
              {subscribedHere && (
                <span className="ml-1 font-semibold text-foreground">
                  · {CADENCE_LABEL[item.cadence]}
                </span>
              )}
            </span>
            <Switch
              checked={subscribedHere}
              onCheckedChange={(next) => onToggleSubscribe(item, next)}
              aria-label={`${subscribeLabel} ${item.name}`}
            />
          </label>
        )}
      </div>
    </article>
  );
}

/**
 * 卡上最多露 `cap` 个词,**按组截断**。
 * ★ 截断发生在组内而不是"整组丢掉":整组丢掉时用户看到的是"这个产品一个词都没有",
 *   而其实只是这张卡摆不下 —— 那会让他去加一批本来就有的词。
 */
function capGroups(
  groups: { title: string; keywords: string[] }[],
  cap: number
): { groups: { title: string; keywords: string[] }[]; hiddenCount: number } {
  const total = groups.reduce((n, g) => n + g.keywords.length, 0);
  if (total <= cap) return { groups, hiddenCount: 0 };
  let left = cap;
  const out: { title: string; keywords: string[] }[] = [];
  for (const g of groups) {
    if (left <= 0) break;
    out.push({ title: g.title, keywords: g.keywords.slice(0, left) });
    left -= Math.min(left, g.keywords.length);
  }
  return { groups: out, hiddenCount: total - cap };
}
